import { useEffect } from "react";
import { useLocale } from "@/store/locale-store";
import { FeatureFlagName, useIsFeatureOn } from "@/queries/feature-flag-query";

/**
 * **금액을 싣는 퍼널 이벤트의 값 축이 둘 다 확정됐는가** (#5190 R1).
 *
 * `useCurrency()` 와 `ProductUtil.getProductPrice` 는 **국가 × 통화** 두 축에 걸려 있고, 둘 다
 * 첫 렌더에서 확정되지 않는다:
 *
 * | 축 | 미확정일 때 값 | 확정 시점 |
 * | --- | --- | --- |
 * | 국가 (`countryCode`) | URL 로케일 파생 (`/en` → `US`) | `_app` 의 `hydrateLocaleStore()` (부모 effect) |
 * | 통화 (`globalEEnabled`) | 로딩 창에 `null` → `geEnabled=false` | 피처플래그 쿼리 resolve |
 *
 * 마운트 시 1회 발화하는 effect 는 **자식이라 부모보다 먼저** 돌므로 둘 다 미확정 상태를 본다.
 *
 * ## 이게 왜 조용한 사고인가
 *
 * 국가 축은 통화가 **같은데 금액만** 다르다 — `countryCode==="US"` 면 `getCountryAwarePrices` 가
 * `[product.usPrice]` 를, 아니면 `product.prices`(USD)를 준다(`ProductUtil.ts`). prod 실측
 * (2026-09-16 · `/api/products?country=US` · 37건): **37/37 제품에서 두 값이 다르다**
 * (`fps-bundle` 790 vs 728). 그래서 `/en` 으로 들어온 GB 사용자 세션이 이렇게 나간다:
 *
 * ```
 * view_item      currency=USD  price=790   ← hydrate 전. countryCode "US" → usPrice
 * add_to_cart    currency=USD  price=728   ← hydrate 후. countryCode "GB" → prices.USD
 * ```
 *
 * **통화 라벨이 같아서** 통화 정합 가드로는 안 잡힌다. 대상은 `/en` + `bh_ccode` 가 US 가 아닌
 * 영어 지원 국가 전부다(`getResolvedCountryCode` 가 URL 언어를 지원하는 국가만 쿠키를 채택하므로,
 * `KR`·`JP`·`CN`·`DE`·`FR`·`ES` 는 `/en` 에서 `US` 로 되돌아와 갈리지 않는다).
 *
 * ## 새 실패 모드를 도입하지 않는다 — `ready` 는 **반드시 true 가 된다**
 *
 * 통화 축이 네트워크 쿼리라 "쿼리가 실패하면 이벤트가 영영 안 나가는" 형태가 될 수 있다.
 * 그렇지 않은 근거: `useIsFeatureOn` 은 **로딩 중에만** `null` 이고, 쿼리가 실패하면
 * `isLoading` 이 false 가 되어 `map[feature]` 의 `undefined` 를 준다 — `!== null` 이므로
 * resolved 로 판정된다(그리고 `useCurrency` 는 fail-safe 로 flag OFF 세계로 흐른다).
 * 즉 실패는 **지연**이지 **소실**이 아니다. `__tests__/libs/analytics/funnel-value-gate.test.tsx`
 * 가 이 불변식을 직접 잠근다 — 이 파일에서 제일 중요한 케이스다.
 */
export function useFunnelValueReady(): boolean {
  const { isCountryHydrated } = useLocale();
  // `null` = 로딩 중. `undefined`(키 없음)·boolean = resolve 끝. 위 docblock 참조.
  const geResolved = useIsFeatureOn(FeatureFlagName.GlobalEEnabled) !== null;
  return isCountryHydrated && geResolved;
}

/**
 * 값 축이 확정된 뒤, **`key` 당 정확히 한 번** 발화한다.
 *
 * - `key` 가 바뀌면 다시 발화한다 — PDP 는 같은 라우트에 머문 채 제품만 바뀌므로
 *   (camo 선택 → `effectiveProduct`, #5068) 그 전환은 **새 조회**가 맞다.
 *   `docs/product-variant-line-policy.md` 기준 camo 는 색상 옵션이 아니라 다른 제품이다.
 * - `key` 가 같으면 `ready` 가 다시 true 가 돼도 발화하지 않는다.
 *
 * ★**deps 에 통화·국가를 넣지 않는다.** `ready` 가 true 로 뒤집히는 렌더의 클로저가 이미
 * 확정값을 들고 있으므로 `fire` 를 그대로 부르면 된다. deps 에 넣으면 사용자가 국가를 바꿀 때
 * 같은 제품 조회가 다시 찍힌다.
 */
export function useFunnelValueEffect(
  key: string | null | undefined,
  fire: () => void,
): void {
  const ready = useFunnelValueReady();

  useEffect(() => {
    if (!ready || !key) return;
    fire();
    // `fire` 는 의도적으로 제외 — 위 ★ 참조. 매 렌더 새 클로저라 넣으면 매번 재실행된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, key]);
}

/**
 * ★**「이미 쐈나」 ref 를 두지 않는다 — deps 하나로 충분하다.**
 *
 * 초판은 `firedKeyRef` 로 같은 key 재발화를 한 번 더 막았는데, **뮤테이션에서 그 가드를
 * 통째로 지워도 빨간불이 안 났다.** `ready` 는 단조증가라(`isLoading` 은 첫 로드에만 true ·
 * hydrate 는 되돌아가지 않는다) `[ready, key]` 가 이미 전환당 1회를 보장하기 때문이다.
 * 검증되지 않는 중복 방어는 **서로의 뮤턴트를 가린다** — 그래서 걷었다.
 *
 * 부수 효과로 동작이 하나 **바뀌었고, 그게 맞다**: `A → B → A`(camo 갔다가 되돌아오기)가
 * ref 가 있을 땐 2회, 지금은 3회 발화한다. GA4 관점에서 되돌아온 것도 **새 조회**다.
 */
